import { useEffect, useMemo } from "react";
import { LoaderCircle } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InfoIcon from "../../../../assets/icons/infor-circle-small-green.svg";
import { FormErrorText } from "../../../../components/form/FormErrorText";
import { Button } from "../../../../components/button";
import { InlineAlert } from "../../../../components/common";
import { MultiSelectV2, type MultiSelectOption } from "../../../../components/multi-select-v2";
import { SearchSelect } from "../../../../components/search-select-v2";
import { SelectV2 } from "../../../../components/select-v2";
import { Checkbox } from "../../../../components/checkbox";
import { DatePickerV3 } from "../../../../components/date-picker-v3";
import { Col } from "../../../../components/col";
import { Row } from "../../../../components/row";
import { getBrowserTimeZone } from "../../../../lib/date-time";
import { DEFAULT_E_GIFT_CARD_FORM_VALUES } from "../../constants/eGiftCardFormConfig";
import { useEGiftCardByMerchantBrandId } from "../../hooks/useEGiftCardByMerchantBrandId";
import { createEGiftCardFormSchema } from "../../schemas/eGiftCardForm.schema";
import type { MerchantEGiftCard } from "../../types/eGiftCard.types";
import type { EGiftCardFormData } from "../../types/eGiftCardForm.types";
import { getDisabledDatesForBrand, hasOverlappingCampaign } from "../../utils/eGiftCardDateRange";
import {
  getEGiftCardCampaignRequestKey,
  toMerchantBrandItemCampaignSelections,
} from "../../utils/eGiftCardCampaignRequest";
import { toDateInTimeZone } from "../../utils/eGiftCardDateTime";
import {
  type MerchantBrandItemOption,
  useGetMerchantBrandItemOptions,
  useGetMerchantBrandOptions,
} from "../../../common";
import { EGiftCardCostSummary } from "./EGiftCardCostSummary";
import EGiftCardQuantityEditor from "./EGiftCardQuantityEditor";

export const E_GIFT_CARD_FORM_ID = "e-gift-card-form";

const getTimezoneOptions = () => {
  const browserTimeZone = getBrowserTimeZone();
  const supportedTimeZones =
    typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];

  return Array.from(new Set([browserTimeZone, ...supportedTimeZones]))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ label: value, value }));
};

const hydrateEditGiftCards = (
  initialGiftCards: MerchantEGiftCard[],
  merchantBrandItemOptions: MerchantBrandItemOption[],
): MerchantEGiftCard[] =>
  initialGiftCards.flatMap((initialGiftCard) => {
    const matchingOption = merchantBrandItemOptions.find(
      (option) =>
        option.merchant_brand_id === initialGiftCard.merchant_brand_id &&
        option.points === initialGiftCard.points,
    );

    if (!matchingOption) {
      return [];
    }

    return [
      {
        ...matchingOption,
        id: initialGiftCard.id,
        coupon_id: initialGiftCard.coupon_id,
        quantity: initialGiftCard.quantity,
        used_quantity: initialGiftCard.used_quantity,
        disabled: true,
        is_existing: true,
      },
    ];
  });

interface EGiftCardFormProps {
  formId?: string;
  isEditMode?: boolean;
  currentEGiftCardId?: string;
  initialValues?: EGiftCardFormData;
  onSubmit: (data: EGiftCardFormData) => void | Promise<void>;
  onSubmissionBlockedChange?: (blocked: boolean) => void;
}

export const EGiftCardForm = ({
  formId = E_GIFT_CARD_FORM_ID,
  isEditMode = false,
  currentEGiftCardId,
  initialValues = DEFAULT_E_GIFT_CARD_FORM_VALUES,
  onSubmit,
  onSubmissionBlockedChange,
}: EGiftCardFormProps) => {
  const { t } = useTranslation();
  const { merchantBrandOptions, fetchMerchantBrandOptions } = useGetMerchantBrandOptions();
  const {
    merchantBrandItemOptions,
    fetchMerchantBrandItemOptions,
    clearMerchantBrandItemOptions,
    isLoading: isLoadingMerchantBrandItems,
    error: merchantBrandItemsError,
  } = useGetMerchantBrandItemOptions();
  const {
    eGiftCards,
    fetchEGiftCardsByMerchantBrandId,
    clearEGiftCards,
    isLoading: isLoadingCampaignValidation,
    error: campaignValidationError,
    validatedRequestKey,
  } = useEGiftCardByMerchantBrandId();
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm<EGiftCardFormData>({
    resolver: zodResolver(
      createEGiftCardFormSchema({
        merchantRequired: t("createEGiftCard.merchantRequired"),
        giftCardRequired: t("createEGiftCard.giftCardRequired"),
        startDateRequired: t("createEGiftCard.startDateRequired"),
        endDateRequired: t("createEGiftCard.endDateRequired"),
        dateRangeInvalid: t("createEGiftCard.dateRangeInvalid"),
      }),
    ),
    defaultValues: initialValues,
  });
  const merchantBrandId = watch("merchantBrandId");
  const giftCardItems = watch("giftCardItems");
  const validFrom = watch("validFrom");

  useEffect(() => {
    void fetchMerchantBrandOptions();
  }, [fetchMerchantBrandOptions]);

  useEffect(() => {
    if (!merchantBrandId) {
      clearMerchantBrandItemOptions();
      return;
    }

    void fetchMerchantBrandItemOptions(merchantBrandId);
  }, [clearMerchantBrandItemOptions, fetchMerchantBrandItemOptions, merchantBrandId]);

  useEffect(() => {
    if (!isEditMode || merchantBrandItemOptions.length === 0) {
      return;
    }

    const hydratedGiftCards = hydrateEditGiftCards(
      initialValues.giftCardItems,
      merchantBrandItemOptions,
    );

    if (hydratedGiftCards.length === 0) {
      return;
    }

    setValue(
      "giftCardIds",
      hydratedGiftCards.map((giftCard) => giftCard.value),
      { shouldDirty: false, shouldTouch: false, shouldValidate: true },
    );
    setValue("giftCardItems", hydratedGiftCards, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [initialValues.giftCardItems, isEditMode, merchantBrandItemOptions, setValue]);

  const campaignMerchantBrandItems = useMemo(
    () => toMerchantBrandItemCampaignSelections(giftCardItems),
    [giftCardItems],
  );
  const campaignRequestKey = getEGiftCardCampaignRequestKey(
    merchantBrandId,
    campaignMerchantBrandItems,
    currentEGiftCardId,
  );
  const isCampaignValidationBlocked = Boolean(
    campaignRequestKey && validatedRequestKey !== campaignRequestKey,
  );

  useEffect(() => {
    onSubmissionBlockedChange?.(isCampaignValidationBlocked);
  }, [isCampaignValidationBlocked, onSubmissionBlockedChange]);

  useEffect(() => {
    if (!merchantBrandId || campaignMerchantBrandItems.length === 0) {
      clearEGiftCards();
      return;
    }

    void fetchEGiftCardsByMerchantBrandId(
      merchantBrandId,
      campaignMerchantBrandItems,
      currentEGiftCardId,
    );
  }, [
    clearEGiftCards,
    currentEGiftCardId,
    fetchEGiftCardsByMerchantBrandId,
    merchantBrandId,
    campaignMerchantBrandItems,
  ]);

  const disabledDates = useMemo(
    () => getDisabledDatesForBrand(merchantBrandId, eGiftCards, currentEGiftCardId),
    [currentEGiftCardId, eGiftCards, merchantBrandId],
  );

  const sortedMerchantOptions = useMemo(
    () => [...merchantBrandOptions].sort((a, b) => a.label.localeCompare(b.label, "en")),
    [merchantBrandOptions],
  );
  const giftCardSelectOptions = useMemo<MultiSelectOption[]>(
    () =>
      merchantBrandItemOptions.map((card) => ({
        value: card.value,
        label: card.label,
        disabled: false,
      })),
    [merchantBrandItemOptions],
  );

  const handleChangeQuantity = (id: string | number, value: number) => {
    if (giftCardItems.length === 0) return;

    const selectedGiftCard = giftCardItems.find((card) => card.id === id);
    if (!selectedGiftCard) return;

    const minimumQuantity = selectedGiftCard.is_existing
      ? (selectedGiftCard.used_quantity ?? 0)
      : 1;

    if (selectedGiftCard.is_existing && value < minimumQuantity) return;

    if (!selectedGiftCard.is_existing && value < 1) {
      const remainingGiftCards = giftCardItems.filter((card) => card.id !== id);
      setValue("giftCardItems", remainingGiftCards);
      setValue(
        "giftCardIds",
        remainingGiftCards.map((card) => card.value),
      );
      return;
    }

    setValue(
      "giftCardItems",
      giftCardItems.map((card) => (card.id === id ? { ...card, quantity: value } : card)),
    );
  };

  const handleRemoveGiftCard = (cardId: string | number) => {
    if (giftCardItems.length === 0) return;

    const remainingGiftCards = giftCardItems.filter((card) => card.id !== cardId);
    setValue("giftCardItems", remainingGiftCards);
    setValue(
      "giftCardIds",
      remainingGiftCards.map((card) => card.value),
    );
  };

  const costSummaryGiftCards = useMemo(
    () =>
      giftCardItems.map((card) => {
        const originalGiftCard = initialValues.giftCardItems.find(
          (initialCard) => initialCard.id === card.id,
        );

        return {
          id: card.id ?? card.value,
          name: (card.name || "").replace(/\s*\(\d+\s*points?\)$/i, ""),
          quantity: card.quantity || 0,
          price: card.faceValue || 0,
          originalQuantity: originalGiftCard?.quantity,
        };
      }),
    [giftCardItems, initialValues.giftCardItems],
  );

  const handleValidatedSubmit = handleSubmit(async (data) => {
    if (isCampaignValidationBlocked) return;

    const validFromInTimeZone = toDateInTimeZone(data.validFrom, data.timezone);
    const validToInTimeZone = toDateInTimeZone(data.validTo, data.timezone);
    if (
      validFromInTimeZone &&
      validToInTimeZone &&
      hasOverlappingCampaign(validFromInTimeZone, validToInTimeZone, eGiftCards, currentEGiftCardId)
    ) {
      setError("validTo", {
        type: "validate",
        message: t("createEGiftCard.campaignDateOverlap"),
      });
      return;
    }

    clearErrors("validTo");
    await onSubmit(data);
  });

  return (
    <form
      id={formId}
      onSubmit={handleValidatedSubmit}
      aria-busy={isCampaignValidationBlocked}
      className="flex-1"
    >
      <div className="space-y-6">
        {/* Merchant and gift card value selection section */}
        <section className="mb-[30px] bg-white dark:bg-gray-800 border border-[#E6E6E6] dark:border-gray-600 rounded-[10px] p-6">
          <h2 className="text-lg text-[#004232] dark:text-white mb-2">
            {t("createEGiftCard.selectMerchant")}
          </h2>
          <hr className="w-full border-[0.8px] border-[#00C49A] dark:border-[#00C49A] mb-6" />

          <Row className="flex-wrap gap-[30px]">
            <Col className="min-w-0 basis-[280px] flex-1 gap-2">
              <label className="text-base text-[#75757D] dark:text-gray-300 block">
                {t("createEGiftCard.chooseBrand")}{" "}
                <span className="text-[#75757D] dark:text-gray-400">*</span>
              </label>
              <Controller
                name="merchantBrandId"
                control={control}
                render={({ field }) => (
                  <SelectV2
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue("giftCardIds", []);
                      setValue("giftCardItems", []);
                    }}
                    options={sortedMerchantOptions}
                    placeholder={t("createEGiftCard.selectBrandPlaceholder")}
                    ariaLabel={t("createEGiftCard.chooseBrand")}
                    disabled={isEditMode}
                    triggerClassName="rounded-lg border-[#DCDCDC] bg-white text-[#75757D] dark:border-gray-600 dark:bg-gray-800 dark:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    contentClassName="bg-white dark:bg-gray-800"
                    optionClassName="cursor-pointer text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                  />
                )}
              />
              <FormErrorText
                message={errors.merchantBrandId?.message}
                className="min-h-[20px]"
              />
            </Col>

            <Col className="min-w-0 basis-[280px] flex-1 gap-[15px]">
              <label className="text-base font-semibold text-[#75757D] dark:text-gray-300 block">
                {t("createEGiftCard.giftCardValue")}{" "}
                <span className="text-[#75757D] dark:text-gray-400">*</span>
              </label>
              <Controller
                name="giftCardIds"
                control={control}
                render={({ field }) => (
                  <MultiSelectV2
                    value={field.value ?? []}
                    onChange={(selected) => {
                      field.onChange(selected);

                      const newGiftCards = selected.flatMap((id): MerchantEGiftCard[] => {
                        const option = merchantBrandItemOptions.find((card) => card.value === id);
                        if (!option) return [];

                        const existingCard = giftCardItems.find((card) => card.value === id);
                        return [
                          {
                            ...option,
                            id: existingCard?.id ?? option.id,
                            used_quantity: existingCard?.used_quantity,
                            disabled: existingCard?.disabled ?? false,
                            is_existing: existingCard?.is_existing ?? false,
                            quantity: existingCard?.quantity ?? 1,
                          },
                        ];
                      });

                      setValue("giftCardItems", newGiftCards);
                    }}
                    onClear={() => {
                      setValue("giftCardIds", []);
                      setValue("giftCardItems", []);
                    }}
                    options={giftCardSelectOptions}
                    disabled={
                      isEditMode ||
                      !merchantBrandId ||
                      isLoadingMerchantBrandItems ||
                      Boolean(merchantBrandItemsError)
                    }
                    ariaLabel={t("createEGiftCard.giftCardValue")}
                    placeholder={
                      merchantBrandId
                        ? t("createEGiftCard.selectGiftCardValuePlaceholder")
                        : t("createEGiftCard.selectMerchantFirstPlaceholder")
                    }
                    searchPlaceholder={t("common.search")}
                    emptyMessage={t("common.no_results")}
                    triggerClassName="min-h-[43px] rounded-lg leading-normal bg-[#FAFAFA] dark:bg-gray-700"
                  />
                )}
              />
              <FormErrorText
                message={errors.giftCardIds?.message}
                className="min-h-[20px]"
              />
              {isLoadingMerchantBrandItems && (
                <div
                  className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
                  role="status"
                >
                  <LoaderCircle
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  {t("createEGiftCard.loadingGiftCardValues")}
                </div>
              )}
              {Boolean(merchantBrandItemsError) && merchantBrandId && (
                <InlineAlert
                  className="mb-0"
                  message={
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{t("createEGiftCard.loadGiftCardValuesFailed")}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void fetchMerchantBrandItemOptions(merchantBrandId)}
                      >
                        {t("ui.retry")}
                      </Button>
                    </div>
                  }
                />
              )}
            </Col>
          </Row>
        </section>

        {/* Gift card quantity section */}
        <section className="mb-[30px] bg-white dark:bg-gray-800 border border-[#E6E6E6] dark:border-gray-600 rounded-[10px] p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-200 pb-3 dark:border-emerald-800">
            <h2 className="text-lg font-medium text-[#004232] dark:text-white">
              {t("createEGiftCard.giftCardQuantity")}
            </h2>
            {giftCardItems.length > 0 && (
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                {giftCardItems.length === 1
                  ? t("createEGiftCard.oneGiftCardSelected")
                  : t("createEGiftCard.multipleGiftCardsSelected", {
                      count: giftCardItems.length,
                    })}
              </span>
            )}
          </div>

          <div className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3.5 dark:border-emerald-900 dark:bg-emerald-950/30">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white dark:bg-emerald-950">
              <img
                className="h-4 w-4"
                src={InfoIcon}
                alt=""
                aria-hidden="true"
              />
            </div>
            <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
              {t("createEGiftCard.quantityInfo")}
            </p>
          </div>

          {giftCardItems.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center dark:border-gray-600 dark:bg-gray-900/30">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("createEGiftCard.noGiftCardsSelected")}
              </p>
            </div>
          ) : (
            <div className="mt-1">
              {giftCardItems.map((card) => {
                const currentQuantity = card.quantity ?? 1;
                const minimumQuantity = card.is_existing ? (card.used_quantity ?? 0) : 1;

                return (
                  <EGiftCardQuantityEditor
                    key={card.id ?? card.value}
                    item={{
                      id: card.id ?? card.value,
                      title: card.reward_name,
                      description: t("createEGiftCard.pointsValue", { count: card.points }),
                      value: currentQuantity,
                      usedQuantity: card.is_existing ? (card.used_quantity ?? 0) : undefined,
                      disableIncrease: false,
                      disableDecrease: isEditMode
                        ? Boolean(card.disabled && currentQuantity <= minimumQuantity)
                        : false,
                      minValue: minimumQuantity,
                      allowRemove: isEditMode ? !card.disabled : true,
                    }}
                    handleChangeValue={handleChangeQuantity}
                    handleRemoveValue={handleRemoveGiftCard}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Campaign date and low-stock settings section */}
        <section className="mb-[30px] bg-white dark:bg-gray-800 border border-[#E6E6E6] dark:border-gray-600 rounded-[10px] p-6">
          <h2 className="text-lg text-[#004232] dark:text-white mb-2">
            {t("createEGiftCard.campaignSettings")}
          </h2>
          <hr className="w-full border-[0.8px] border-[#00C49A] dark:border-[#00C49A] mb-6" />

          <Row className="gap-[30px] items-start flex-wrap">
            <Col className="w-full gap-[15px] sm:w-[322px]">
              <label className="text-base font-semibold text-[#75757D] dark:text-gray-300 block">
                {t("createEGiftCard.startDate")}{" "}
                <span className="text-[#75757D] dark:text-gray-400">*</span>
              </label>
              <Controller
                name="validFrom"
                control={control}
                render={({ field }) => (
                  <DatePickerV3
                    value={field.value}
                    initialSelectedDate={field.value}
                    onChange={field.onChange}
                    className="h-[43px] w-full bg-[#FAFAFA] dark:bg-gray-800 border-[#DCDCDC] dark:border-gray-600 rounded-lg"
                    placeholder="mm/dd/yyyy"
                    dateFormat="MM/dd/yyyy hh:mm aa"
                    primaryColor="#00C49A"
                    disableToDate={new Date(Date.now() - 86400000)}
                    customDisabledDates={disabledDates}
                    disabled={isEditMode || isCampaignValidationBlocked}
                    ariaLabel={t("createEGiftCard.startDate")}
                  />
                )}
              />
              <FormErrorText
                message={errors.validFrom?.message}
                className="min-h-[20px]"
              />
            </Col>

            <Col className="w-full gap-[15px] sm:w-[322px]">
              <label className="text-base font-semibold text-[#75757D] dark:text-gray-300 block">
                {t("createEGiftCard.endDate")}{" "}
                <span className="text-[#75757D] dark:text-gray-400">*</span>
              </label>
              <Controller
                name="validTo"
                control={control}
                render={({ field }) => (
                  <DatePickerV3
                    value={field.value}
                    initialSelectedDate={field.value}
                    onChange={(date) => {
                      if (!date) {
                        field.onChange(date);
                        return;
                      }

                      const endOfDay = new Date(date);
                      endOfDay.setHours(23, 59, 59, 999);
                      field.onChange(endOfDay);
                    }}
                    className="h-[43px] w-full bg-[#FAFAFA] dark:bg-gray-800 border-[#DCDCDC] dark:border-gray-600 rounded-lg"
                    placeholder="mm/dd/yyyy"
                    dateFormat="MM/dd/yyyy hh:mm aa"
                    primaryColor="#00C49A"
                    disableToDate={
                      validFrom
                        ? new Date(
                            validFrom.getFullYear(),
                            validFrom.getMonth(),
                            validFrom.getDate() - 1,
                          )
                        : new Date(Date.now() - 86400000)
                    }
                    customDisabledDates={disabledDates}
                    disabled={isCampaignValidationBlocked}
                    ariaLabel={t("createEGiftCard.endDate")}
                  />
                )}
              />
              <FormErrorText
                message={errors.validTo?.message}
                className="min-h-[20px]"
              />
            </Col>

            <Col className="w-full gap-[15px] sm:w-[322px]">
              <label className="text-base font-semibold text-[#75757D] dark:text-gray-300 block">
                {t("createEGiftCard.timezone")}
              </label>
              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <SearchSelect
                    value={field.value || ""}
                    onChange={field.onChange}
                    options={timezoneOptions}
                    placeholder={t("createEGiftCard.selectTimezonePlaceholder")}
                    searchPlaceholder={t("createEGiftCard.searchTimezonePlaceholder")}
                    emptyMessage={t("createEGiftCard.timezoneNotFound")}
                    ariaLabel={t("createEGiftCard.timezone")}
                    showClear={false}
                    side="top"
                    triggerClassName="h-[43px] rounded-lg border-[#DCDCDC] bg-[#FAFAFA] text-[#75757D] dark:!bg-gray-800 dark:border-gray-600 dark:text-white"
                  />
                )}
              />
              <FormErrorText
                message={errors.timezone?.message}
                className="min-h-[20px]"
              />
            </Col>

            <Col className="flex flex-col flex-1">
              <div className="h-[70px] flex flex-col border-t border-b border-transparent pt-[24px]">
                <div className="flex items-center gap-2 mb-1">
                  <label
                    htmlFor="e-gift-card-alert-low-stock"
                    className="text-base font-semibold text-[#75757D] dark:text-gray-300"
                  >
                    {t("createEGiftCard.alertLowStock")}
                  </label>
                  <img
                    className="w-[18px] h-[18px] dark:brightness-0 dark:invert"
                    src={InfoIcon}
                    alt="Alert info"
                    title={t("createEGiftCard.alertLowStockTooltip")}
                  />
                </div>
                <div className="mt-2">
                  <Controller
                    name="isAlertLowStock"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="e-gift-card-alert-low-stock"
                        className="alert-low-stock cursor-pointer dark:bg-gray-700 h-6 w-6 rounded-md"
                        checked={Boolean(field.value)}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    )}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {campaignRequestKey && isLoadingCampaignValidation && (
            <div
              className="mt-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200"
              role="status"
            >
              <LoaderCircle
                className="h-4 w-4 animate-spin"
                aria-hidden="true"
              />
              {t("createEGiftCard.checkingCampaignDates")}
            </div>
          )}

          {campaignRequestKey && Boolean(campaignValidationError) && (
            <InlineAlert
              className="mb-0 mt-4"
              message={
                <div className="flex flex-wrap items-center gap-2">
                  <span>{t("createEGiftCard.campaignDateCheckFailed")}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void fetchEGiftCardsByMerchantBrandId(
                        merchantBrandId,
                        campaignMerchantBrandItems,
                        currentEGiftCardId,
                      )
                    }
                  >
                    {t("ui.retry")}
                  </Button>
                </div>
              }
            />
          )}
        </section>

        {/* Gift card cost summary section */}
        <EGiftCardCostSummary
          items={costSummaryGiftCards}
          isEditMode={isEditMode}
        />
      </div>
    </form>
  );
};
